import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { G } from "@/brand";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Copy, Check, Wand2, Lock, ExternalLink, Music2 } from "lucide-react";

export function PromptDetail({ prompt, onClose, previewTier = "" }) {
  const { user, setUser, requireAuth } = useAuth();
  const nav = useNavigate();
  const [text, setText] = useState(prompt.text || "");
  const [state, setState] = useState("idle"); // idle|loading|ready|locked|error
  const [msg, setMsg] = useState("");
  const [copied, setCopied] = useState(false);
  const [instruction, setInstruction] = useState("");
  const [editing, setEditing] = useState(false);
  const [editResult, setEditResult] = useState("");
  const [editable, setEditable] = useState(prompt.editable);
  const previewParams = previewTier ? { preview_tier: previewTier } : {};

  useEffect(() => {
    const load = async () => {
      if (prompt.access_level === "free") { setText(prompt.text); setState("ready"); return; }
      if (!requireAuth("Sign in to open premium prompts.")) { onClose(); return; }
      setState("loading");
      try {
        const { data } = await api.get(`/prompts/${prompt.id}`, { params: previewParams });
        setText(data.text);
        setEditable(data.editable);
        if (user) setUser({ ...user, credit_balance: data.credit_balance });
        setState("ready");
        if (data.charged > 0) setMsg(`−${data.charged} credits`);
      } catch (e) {
        const code = e.response?.status;
        setMsg(apiError(e.response?.data?.detail) || e.message);
        setState(code === 403 || code === 402 ? "locked" : "error");
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copy = () => {
    navigator.clipboard.writeText(editResult || text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const copyAndOpen = (url, name) => {
    navigator.clipboard.writeText(editResult || text);
    setMsg(`Copied — paste into ${name}`);
    window.open(url, "_blank", "noopener");
  };

  const logTrack = async () => {
    if (!requireAuth("Sign in to log tracks to your production journal.")) { onClose(); return; }
    try {
      await api.post("/tracks", {
        title: `${prompt.genre_name} · ${prompt.revision}`,
        platform: "Suno", prompt_id: prompt.id, genre_name: prompt.genre_name,
        notes: "", link: "",
      });
      setMsg("Saved to My Tracks — add notes in your dashboard");
    } catch (e) {
      setMsg("Could not save track");
    }
  };

  const runEdit = async () => {
    if (!instruction.trim()) return;
    setEditing(true); setMsg("");
    try {
      const { data } = await api.post(`/prompts/${prompt.id}/edit`, { instruction }, { params: previewParams });
      setEditResult(data.result);
      if (user) setUser({ ...user, credit_balance: data.credit_balance });
      setMsg(`AI refined · −${data.charged} credits`);
    } catch (e) {
      setMsg(apiError(e.response?.data?.detail) || e.message);
    } finally { setEditing(false); }
  };

  return (
    <div data-testid="prompt-detail" onClick={onClose} style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...box, position: "relative" }}>
        <button onClick={onClose} data-testid="prompt-close" style={closeBtn}>×</button>

        <div className="mono" style={{ fontSize: 9, letterSpacing: "0.4em", color: prompt.access_level === "upgrade_2" ? G.orange : G.gold, marginBottom: 6 }}>
          {prompt.genre_name?.toUpperCase()} · {prompt.category?.toUpperCase()}
        </div>
        <div style={{ fontSize: 22, fontWeight: 300, fontStyle: "italic", color: "#fff", marginBottom: 18 }}>
          {prompt.revision} · {prompt.subcategory || "Signature"}
        </div>

        {state === "loading" && <div className="spinner" />}

        {state === "locked" && (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <Lock size={28} color={G.gold} style={{ margin: "0 auto 14px" }} />
            <div style={{ color: G.muted, fontStyle: "italic", fontSize: 15, marginBottom: 20 }}>{msg}</div>
            <button data-testid="prompt-upgrade-cta" onClick={() => { onClose(); nav("/pricing"); }} className="btn-gold" style={{ padding: "12px 28px", fontSize: 10 }}>
              View Upgrades
            </button>
          </div>
        )}

        {state === "ready" && (
          <>
            <div data-testid="prompt-text" style={{ background: G.black, border: `1px solid ${G.border}`, padding: 18, fontSize: 14.5, lineHeight: 1.7, color: G.text, maxHeight: 230, overflowY: "auto" }}>
              {editResult || text}
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 14, alignItems: "center", flexWrap: "wrap" }}>
              <button data-testid="prompt-copy" onClick={copy} className="btn-outline" style={{ padding: "10px 18px", fontSize: 10, display: "flex", alignItems: "center", gap: 8 }}>
                {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy"}
              </button>
              <button data-testid="prompt-open-suno" onClick={() => copyAndOpen("https://suno.com/create", "Suno")} className="btn-gold" style={{ padding: "10px 16px", fontSize: 10, display: "flex", alignItems: "center", gap: 7 }}>
                <ExternalLink size={12} /> Copy &amp; Open Suno
              </button>
              <button data-testid="prompt-open-udio" onClick={() => copyAndOpen("https://www.udio.com/create", "Udio")} className="btn-outline" style={{ padding: "10px 16px", fontSize: 10, display: "flex", alignItems: "center", gap: 7 }}>
                <ExternalLink size={12} /> Copy &amp; Open Udio
              </button>
              <button data-testid="prompt-log-track" onClick={logTrack} className="btn-ghost" style={{ padding: "10px 16px", fontSize: 10, display: "flex", alignItems: "center", gap: 7 }}>
                <Music2 size={12} /> Log Track
              </button>
              {msg && <span className="mono" style={{ fontSize: 10, color: G.gold, letterSpacing: ".08em" }}>{msg}</span>}
            </div>

            {editable && (
              <div style={{ marginTop: 22, borderTop: `1px solid ${G.border}44`, paddingTop: 18 }}>
                <div className="mono" style={{ fontSize: 9, letterSpacing: "0.3em", color: G.gold, marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                  <Wand2 size={13} /> AI REFINE · 10 CR FIRST EDIT, 5 AFTER
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <input data-testid="edit-instruction" value={instruction} onChange={(e) => setInstruction(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && runEdit()}
                    placeholder='e.g. "make it darker, drop tempo to 100 BPM"'
                    style={{ flex: 1, padding: "11px 14px", background: G.panel, border: `1px solid ${G.border}`, color: G.text, fontSize: 13 }} />
                  <button data-testid="edit-run" onClick={runEdit} disabled={editing || !instruction} className="btn-gold" style={{ padding: "0 20px", fontSize: 10 }}>
                    {editing ? "…" : "Refine"}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const overlay = { position: "fixed", inset: 0, zIndex: 998, background: "rgba(0,0,0,.85)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 };
const box = { background: G.dark, border: `1px solid ${G.borderHot}`, padding: "40px 36px", maxWidth: 640, width: "100%" };
const closeBtn = { position: "absolute", top: 14, right: 20, background: "none", border: "none", color: G.muted, cursor: "pointer", fontSize: 24, lineHeight: 1 };
